import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Body2, Caption1, Spinner,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCustomerProgress } from '../../../api/hooks';
import type { CustomerProgressItem, CustomerProgressEpic } from '../../../api/types';

// FEAT-025 Customer Progress. Three-column board per Epic: Done / Underway / Next.
// Phone-friendly grid (Title3 stays legible at narrow widths thanks to Fluent's
// responsive typography defaults).
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  epicCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 720px)': { gridTemplateColumns: '1fr' },
  },
  column: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  columnHeader: { color: tokens.colorNeutralForeground2 },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
});

function Column({ title, items }: { title: string; items: CustomerProgressItem[] }) {
  const styles = useStyles();
  return (
    <div className={styles.column}>
      <Body1 className={styles.columnHeader}><strong>{title}</strong> ({items.length})</Body1>
      {items.length === 0 ? (
        <Caption1>Nothing here yet.</Caption1>
      ) : items.map((i, idx) => (
        <div key={`${title}-${idx}`} className={styles.item}>
          <Body2>{i.customerTitle}</Body2>
          {i.customerDescription ? <Caption1>{i.customerDescription}</Caption1> : null}
        </div>
      ))}
    </div>
  );
}

export function CustomerProgressPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useCustomerProgress(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Your Project's Progress</Title2>
        <Body1 className={styles.hint}>
          What's done, what's underway, what's next — per initiative, in business language.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project to see how it's tracking.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading progress…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.epics.length === 0 ? (
        <Body1>Nothing to show yet.</Body1>
      ) : (
        data.epics.map((e: CustomerProgressEpic, idx: number) => (
          <div key={idx} className={styles.epicCard}>
            <Title3>{e.customerTitle}</Title3>
            {e.customerDescription ? <Body1>{e.customerDescription}</Body1> : null}
            <div className={styles.columns}>
              <Column title="Done" items={e.done} />
              <Column title="Underway" items={e.underway} />
              <Column title="Next" items={e.next} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
